import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch } from '../../services/api';

const MAX_MB = 25;
const ACCEPTED = '.pdf,.jpg,.jpeg,.png,.heic';

export default function UploadDocModal({ group, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const requiresUserExpiry = group.key === 'proof_of_id';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please choose a file to upload.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large (max ${MAX_MB} MB).`);
      return;
    }
    if (requiresUserExpiry && !expiresAt) {
      toast.error('Please enter the expiry date shown on the document.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('category', group.key);
      formData.append('file', file);
      if (requiresUserExpiry && expiresAt) {
        formData.append('expires_at', expiresAt);
      }
      const res = await authFetch('/api/documents/personal', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      onUploaded();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md rounded-2xl bg-slate-900 p-6 border border-slate-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Upload — {group.label}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-slate-400 mb-4">{group.description}</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">File</label>
              <input
                type="file"
                accept={ACCEPTED}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-slate-300"
              />
              <p className="text-xs text-slate-500 mt-1">
                PDF, JPG, PNG, or HEIC. Max {MAX_MB} MB.
              </p>
            </div>
            {requiresUserExpiry && (
              <div>
                <label className="block text-sm text-slate-400 mb-1">
                  Document expiry date
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  required
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the date printed on the document (passport / driving licence
                  expiry).
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                {submitting ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
